import { ref, computed, onBeforeUnmount, nextTick } from 'vue';
import { useAppState } from './useAppState';
import { useQuizApi } from './useQuizApi';
import { useCustomer } from './useCustomer';
export function useQuiz(options = {}) {
    const state = useAppState();
    const quizApi = useQuizApi();
    const customer = useCustomer();
    const quizActive = ref(true);
    const activeQuestion = ref(null);
    const answerResponse = ref(null);
    const activeQuestionAnswered = ref(null);
    const openToStats = ref(false);
    const autoOpenTimeout = ref(null);
    const autoOpenCookieKey = ref(null);
    const shouldShowQuiz = computed(() => {
        if (!state.quizzes || state.quizzes.length === 0 || !state.quizzes[0]) {
            return false;
        }
        const quiz = state.quizzes[0];
        const now = new Date();
        if (quiz.advertiseDateTime) {
            const advertiseDate = new Date(quiz.advertiseDateTime);
            if (now < advertiseDate) {
                return false;
            }
        }
        if (quiz.showClosedUntilDateTime) {
            const showClosedUntil = new Date(quiz.showClosedUntilDateTime);
            if (now > showClosedUntil) {
                return false;
            }
        }
        return true;
    });
    function isQuestionToday(question) {
        if (!question || !question.startDateTime || !question.endDateTime) {
            return false;
        }
        const now = new Date();
        const startDate = new Date(question.startDateTime);
        const endDate = new Date(question.endDateTime);
        return now >= startDate && now <= endDate;
    }
    function cancelAutoOpen() {
        if (autoOpenTimeout.value) {
            clearTimeout(autoOpenTimeout.value);
            autoOpenTimeout.value = null;
            autoOpenCookieKey.value = null;
        }
    }
    function loadQuizData() {
        activeQuestionAnswered.value = null;
        // Test mode: load from imported JSON instead of API
        if (options.quizTestMode && options.quizTestData) {
            const quiz = JSON.parse(JSON.stringify(options.quizTestData));
            const questionIndex = (options.quizTestQuestionNumber || 1) - 1;
            const question = quiz.questions[questionIndex];
            if (question) {
                const now = new Date();
                question.startDateTime = new Date(now.getTime() - 3600000).toISOString();
                question.endDateTime = new Date(now.getTime() + 3600000).toISOString();
            }
            quiz.advertiseDateTime = new Date(Date.now() - 86400000).toISOString();
            quiz.startDateTime = new Date(Date.now() - 3600000).toISOString();
            quiz.endDateTime = new Date(Date.now() + 3600000).toISOString();
            quiz.showClosedUntilDateTime = new Date(Date.now() + 86400000).toISOString();
            state.quizzes = [quiz];
            activeQuestion.value = question;
            state.quizPoints = { answered: 0, correct: 0, total: 0, streakMeter: 0, streakMeterInterval: quiz.streakMeterInterval };
            state.quizAnswers = [];
            activeQuestionAnswered.value = false;
            return;
        }
        quizApi.getQuizzes(true, false, false, () => {
            if (!state.quizzes || state.quizzes.length === 0) {
                return;
            }
            activeQuestion.value = state.quizzes[0]?.questions.find((q) => isQuestionToday(q));
            quizApi.getQuizPoints(state.quizzes[0]?.id, () => {
                quizApi.getQuizAnswers(state.quizzes[0]?.id, () => {
                    activeQuestionAnswered.value = false;
                    if (activeQuestion.value) {
                        const thisQuestionAnswered = state.quizAnswers?.find((a) => a.question.id === activeQuestion.value.id);
                        activeQuestionAnswered.value = thisQuestionAnswered && thisQuestionAnswered.selectedAnswer != null;
                        // Auto-open drawer if quiz is active, question not answered, and not auto-opened before for this question
                        const cookieKey = `quiz_auto_opened_${activeQuestion.value.id}`;
                        const hasAutoOpened = options.cookies?.get(cookieKey);
                        if (!activeQuestionAnswered.value && shouldShowQuiz.value && !hasAutoOpened) {
                            // Clean up old quiz auto-open cookies before setting new one
                            const allCookies = options.cookies?.keys() || [];
                            allCookies.forEach((key) => {
                                if (key.startsWith('quiz_auto_opened_') && key !== cookieKey) {
                                    options.cookies?.remove(key);
                                }
                            });
                            autoOpenCookieKey.value = cookieKey;
                            autoOpenTimeout.value = setTimeout(() => {
                                if (options.refs?.quizPlayDrawer?.value) {
                                    options.cookies?.set(cookieKey, 'true', '1d');
                                    autoOpenCookieKey.value = null;
                                    nextTick(() => {
                                        if (options.handleAnswerClick) {
                                            options.handleAnswerClick();
                                        }
                                    });
                                }
                            }, 1000);
                        }
                    }
                });
            });
        });
    }
    function handleRulesClick() {
        cancelAutoOpen();
        options.refs?.quizRulesDrawer?.value?.openDrawer();
    }
    function handleAnswerClick() {
        cancelAutoOpen();
        openToStats.value = false;
        nextTick(() => {
            options.refs?.quizPlayDrawer?.value?.openDrawer();
        });
    }
    function handleShowStatsClick() {
        cancelAutoOpen();
        openToStats.value = true;
        nextTick(() => {
            options.refs?.quizPlayDrawer?.value?.openDrawer();
        });
    }
    function handleHome() {
        document.body.classList.remove('hide-nav-bar');
        activeQuestionAnswered.value = null;
        if (options.quizTestMode) {
            activeQuestion.value = state.quizzes?.[0]?.questions.find((q) => isQuestionToday(q));
            activeQuestionAnswered.value = true;
            return;
        }
        quizApi.getQuizzes(true, false, false, () => {
            if (!state.quizzes || state.quizzes.length === 0)
                return;
            activeQuestion.value = state.quizzes[0]?.questions.find((q) => isQuestionToday(q));
            quizApi.getQuizPoints(state.quizzes[0].id);
            quizApi.getQuizAnswers(state.quizzes[0].id, () => {
                if (activeQuestion.value) {
                    const thisQuestionAnswered = state.quizAnswers?.find((a) => a.question.id === activeQuestion.value.id);
                    activeQuestionAnswered.value = thisQuestionAnswered && thisQuestionAnswered.selectedAnswer != null;
                }
                else {
                    activeQuestionAnswered.value = true;
                }
            });
        });
    }
    function handleSubmitAnswer(quiz, answer) {
        answerResponse.value = null;
        const question = quiz.questions.find((q) => isQuestionToday(q));
        if (options.quizTestMode) {
            answerResponse.value = {
                isCorrect: answer.isCorrect,
                answerDescription: question.answerDescription,
                answerExample: question.answerExample,
                percentageCorrectCustomers: 65,
                points: {
                    answered: (state.quizPoints?.answered || 0) + 1,
                    correct: (state.quizPoints?.correct || 0) + (answer.isCorrect ? 1 : 0),
                    total: (state.quizPoints?.total || 0) + (answer.isCorrect ? question.correctPoints : question.incorrectPoints),
                    streakMeter: (state.quizPoints?.streakMeter || 0) + 1
                }
            };
            return;
        }
        quizApi.postQuizAnswer(quiz.id, question.id, answer.id, (response) => {
            quizApi.clearQuizCache(quiz.id);
            answerResponse.value = response;
        });
    }
    function handleTermsClick() {
        options.refs?.quizTermsDrawer?.value?.openDrawer();
    }
    function handleNotificationsChange(enabled) {
        if (!state.customer?.preferences) {
            state.customer.preferences = {
                dailyCurrentBalanceNotifications: false,
                dailyCreditBalanceNotifications: false,
                quizNotifications: false
            };
        }
        state.customer.preferences.quizNotifications = enabled;
        customer.savePreferences(() => {
            console.log('Quiz notifications preference saved:', enabled);
        });
    }
    function handleQuizDrawerOpened() {
        document.body.classList.add('hide-nav-bar');
    }
    function handleQuizDrawerClosed() {
        document.body.classList.remove('hide-nav-bar');
    }
    // Lifecycle: clean up on unmount
    onBeforeUnmount(() => {
        document.body.classList.remove('hide-nav-bar');
        if (autoOpenTimeout.value) {
            clearTimeout(autoOpenTimeout.value);
            autoOpenTimeout.value = null;
        }
        if (autoOpenCookieKey.value && options.cookies) {
            options.cookies.remove(autoOpenCookieKey.value);
            autoOpenCookieKey.value = null;
        }
    });
    return {
        quizActive,
        activeQuestion,
        answerResponse,
        activeQuestionAnswered,
        openToStats,
        shouldShowQuiz,
        isQuestionToday,
        loadQuizData,
        handleRulesClick,
        handleAnswerClick,
        handleShowStatsClick,
        handleHome,
        handleSubmitAnswer,
        handleTermsClick,
        handleNotificationsChange,
        handleQuizDrawerOpened,
        handleQuizDrawerClosed,
    };
}
